/**
 * AddToLibraryModal — modal for saving a game to the library.
 *
 * Shown at end of game (Game.tsx) or end of training deal (TrainingPage.tsx).
 * Pre-fills with tag "à classer" and lets the user add tags + initial note.
 */

import { useState, useCallback, useEffect } from 'react';
import type { GameHistory } from '../../game/gameHistory';
import type {
  LibraryGameData,
  LibraryPlayerInfo,
  LibraryOriginType,
  LibraryOriginMetadata,
  AddToLibraryPayload,
} from '../../types/library';
import { PREDEFINED_TAGS } from '../../types/library';
import { BACKEND_URL } from '../../config';

interface AddToLibraryModalProps {
  pseudo: string;
  history: GameHistory;
  originType: LibraryOriginType;
  originId?: string;
  originDonneNum?: number;
  originMetadata?: LibraryOriginMetadata;
  myPosition: number;
  isPartial?: boolean;
  partialPhase?: number;
  partialTrickNum?: number;
  onClose: () => void;
  onSaved?: (gameId: number) => void;
}

function gameHistoryToLibraryData(history: GameHistory): LibraryGameData {
  const players: LibraryPlayerInfo[] = history.players.map(p => ({
    position: p.position,
    displayName: p.name,
    isHuman: !p.name.startsWith('Bot_'),
    isBot: p.name.startsWith('Bot_'),
  }));

  const initialHands: Record<number, number[]> = {};
  for (const [pos, hand] of history.initialHands) {
    initialHands[pos] = [...hand];
  }

  const poignees: Record<number, number[]> = {};
  if (history.poignees) {
    for (const p of history.poignees) {
      poignees[p.player] = [...p.cards];
    }
  }

  const s = history.score as Record<string, any> | undefined;

  return {
    players,
    playerCount: history.playerCount,
    dealer: history.dealer,
    taker: history.taker,
    contract: history.contract,
    initialHands,
    chien: [...history.chien],
    ecart: [...history.ecart],
    bids: history.bids.map(b => ({ player: b.player, bid: b.bid, timestamp: b.timestamp })),
    tricks: history.tricks.map(t => ({
      trickNumber: t.trickNumber,
      leader: t.leader,
      cards: t.cards.map(c => ({ player: c.player, card: c.card, timestamp: c.timestamp })),
      winner: t.winner,
    })),
    poignees,
    score: s ? {
      takerPoints: s.takerPoints ?? s.points_joueur ?? s.pointsAttaque ?? 0,
      defensePoints: s.defensePoints ?? s.points_defense ?? 0,
      oudlers: s.oudlers ?? s.nbBouts ?? 0,
      targetPoints: s.requiredPoints ?? s.pointsNecessaires ?? 56,
      difference: s.difference ?? 0,
      contract: s.contract ?? s.contrat ?? 0,
      multiplier: s.multiplier ?? s.multiplicateur ?? 1,
      poigneeBonus: s.poigneeBonus ?? s.poignee ?? 0,
      petitAuBout: s.petitAuBout ?? 0,
      slamBonus: s.slamBonus ?? s.chelem ?? 0,
      finalScores: s.playerScores ?? s.scores ?? [],
    } : undefined,
  };
}

export default function AddToLibraryModal({
  pseudo,
  history,
  originType,
  originId,
  originDonneNum,
  originMetadata,
  myPosition,
  isPartial,
  partialPhase,
  partialTrickNum,
  onClose,
  onSaved,
}: AddToLibraryModalProps) {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [customTags, setCustomTags] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch user's custom tags
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/library/tags?pseudo=${encodeURIComponent(pseudo)}`)
      .then(r => r.json())
      .then(data => setCustomTags(data.custom ?? []))
      .catch(() => {});
  }, [pseudo]);

  const allTags = [...PREDEFINED_TAGS, ...customTags];

  const toggleTag = useCallback((tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag],
    );
  }, []);

  const handleSave = useCallback(async () => {
    if (saving || saved) return;
    setSaving(true);
    setError(null);

    try {
      const gameData = gameHistoryToLibraryData(history);
      const finalTags = selectedTags.length > 0 ? selectedTags : ['à classer'];
      const payload: AddToLibraryPayload = {
        game_data: gameData,
        origin_type: originType,
        origin_id: originId,
        origin_donne_num: originDonneNum,
        origin_metadata: originMetadata,
        recommended_position: myPosition,
        is_partial: isPartial,
        partial_phase: partialPhase,
        partial_trick_num: partialTrickNum,
        tags: finalTags,
        note: note.trim() || undefined,
      };

      const res = await fetch(
        `${BACKEND_URL}/api/library/games?pseudo=${encodeURIComponent(pseudo)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      );

      if (res.ok) {
        const data = await res.json();
        setSaved(true);
        onSaved?.(data.id);
        sessionStorage.setItem('library_needs_refresh', '1');
        setTimeout(onClose, 800);
      } else {
        const body = await res.text().catch(() => '');
        console.warn('[Library] Save failed:', res.status, body);
        setError(`Erreur ${res.status} — ${body || 'impossible d\'enregistrer'}`);
      }
    } catch (err) {
      console.warn('[Library] Save error:', err);
      setError('Erreur réseau — vérifiez votre connexion');
    } finally {
      setSaving(false);
    }
  }, [saving, saved, history, originType, originId, originDonneNum, originMetadata, myPosition, isPartial, partialPhase, partialTrickNum, selectedTags, note, pseudo, onClose, onSaved]);

  return (
    <div className="library-modal-overlay" onClick={onClose}>
      <div className="library-modal" onClick={e => e.stopPropagation()}>
        <div className="library-modal-header">
          <h3>Ajouter à la bibliothèque</h3>
          <button className="library-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="library-modal-body">
          {/* Tags */}
          <div className="library-modal-section">
            <label className="library-modal-label">Tags</label>
            <div className="library-modal-tags">
              {allTags.map(tag => (
                <button
                  key={tag}
                  className={`library-tag ${selectedTags.includes(tag) ? 'active' : ''}`}
                  data-tag={tag}
                  onClick={() => toggleTag(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Note */}
          <div className="library-modal-section">
            <label className="library-modal-label">Commentaire</label>
            <textarea
              className="library-modal-textarea"
              placeholder="Note sur cette partie…"
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <div className="library-modal-footer">
          {error && (
            <div className="library-modal-error">{error}</div>
          )}
          <div className="library-modal-buttons">
            <button className="library-modal-btn secondary" onClick={onClose}>
              Annuler
            </button>
            <button
              className={`library-modal-btn primary ${saved ? 'saved' : ''}`}
              onClick={handleSave}
              disabled={saving || saved}
            >
              {saved ? 'Ajoutée ✓' : saving ? 'Enregistrement…' : 'Ajouter'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
